import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import * as React from "react";
import { useRef } from "react";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EditIcon from "@mui/icons-material/Edit";
import {
    DonationBooleanTypeList,
    DonationTypeList,
    PictureBooleanTypeList,
    StoreOpeningConditionsBooleanTypeList,
    StoreOpeningConditionsTypeList,
    ConflictBooleanTypeList,
    LocationImpactBooleanTypeList,
} from "../../data/type";
import { setPermissionFunc } from "../components/PermissionFunc";
import { useAppSelector } from "../../app/hooks";
import {
    BGDefaultColor,
    BGChangeColor,
    BGDisabledColor,
    BGErrorColor,
    TextDefaultColor,
    TextChangeColor,
    TextDisabledColor,
    BorderDefaultColor,
    BorderHoverColor,
    BorderFocusedColor,
    BorderErrorColor
} from "../../data/table";
import { textColor } from "../../data/tableFunc";
import {
    chatBubbleIconSx,
    editIconCircleSx,
    iconHover,
} from "./ChatBubbleAndEditIconStyle";
import { LOCAL_CONSTANT } from "../Const";
import {
    setTablePhysicsName,
    pageToTableMapFunc,
    isIpad
} from "../Common";

export interface InputProps {
    //全ページ対応できるように各ページ型を追加していく
    props: {
        userType: string;
        disabled: boolean;
        pageName: string;
        labelName: string;
        startAdornment: string;
        endAdornment: string;
        InputValueObj:
            | StoreOpeningConditionsTypeList
            | DonationTypeList
            | PictureBooleanTypeList
            | any;
        ReadOnlyObj:
            | StoreOpeningConditionsBooleanTypeList
            | DonationBooleanTypeList
            | PictureBooleanTypeList
            | ConflictBooleanTypeList
            | any;
        setReadOnlyObj:
            | React.Dispatch<React.SetStateAction<StoreOpeningConditionsBooleanTypeList>>
            | React.Dispatch<React.SetStateAction<DonationBooleanTypeList>>
            | React.Dispatch<React.SetStateAction<PictureBooleanTypeList>>
            | React.Dispatch<React.SetStateAction<ConflictBooleanTypeList>>
            | React.Dispatch<React.SetStateAction<LocationImpactBooleanTypeList>>;
        ShowEditIconObj:
            | StoreOpeningConditionsBooleanTypeList
            | DonationBooleanTypeList
            | PictureBooleanTypeList
            | ConflictBooleanTypeList
            | LocationImpactBooleanTypeList;
        ShowIconsObj:
            | StoreOpeningConditionsBooleanTypeList
            | DonationBooleanTypeList
            | PictureBooleanTypeList
            | ConflictBooleanTypeList
            | LocationImpactBooleanTypeList
            | any;
        handleChatBubbleClick: (labelName: string) => void;
        handleInputBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>, labelName: string) => void;
        handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, labelName: string) => void;
        handleInputClick: (labelName: string) => void;
    };
}

export const InputWithIcons: React.FC<InputProps> = ({ props }) => {
    const objLocationBase = useAppSelector(
        (state) => state.longPage.locationBase
    );
    const objRrBranchLog = useAppSelector(
        (state) => state.longPage.rrBranchLog
    );
    const objRequestErrorList = useAppSelector(
        (state) => state.longPage.requestErrorList
    );
    const listDeficiencyComment = useAppSelector(
        (state) => state.deficiencyComment
    );
    // 鉛筆をクリックしたときのフォーカス制御
    const inputRef = useRef<HTMLInputElement | null>(null);

    // 鉛筆をクリックしたとき、Input編集可
    const handleEditIconClick = (labelName: string) => {
        if (
            setPermissionFunc(
                props.userType,
                objLocationBase.task_id,
                props.pageName,
                labelName
            ) === false
        ) {
            //falseにすると、Input編集可
            props.setReadOnlyObj({ ...props.ReadOnlyObj, [labelName]: false });
            
            // iPad対応：z001（RR）かつiPadの場合はフォーカスを当てない
            const isRRUserOnIpad = props.userType === LOCAL_CONSTANT.USER_TYPE.RR && isIpad();
            if (!isRRUserOnIpad) {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }
        }
    };

    const changeTextColor = () => {
        let changeColor = false;
        let color = TextDefaultColor;
        changeColor = textColor(
            objRrBranchLog,
            props.pageName,
            props.labelName
        );
        if (changeColor) {
            color = TextChangeColor;
        } else {
            if (!LOCAL_CONSTANT.USER_TYPE.RFC_RR.includes(props.userType)) {
                /* ユーザー種別がRFCとリサーチ以外の場合*/
                color = TextDisabledColor;
            } else if (props.userType === LOCAL_CONSTANT.USER_TYPE.RFC) {
                color = props.disabled ? TextDisabledColor : TextDefaultColor;
            } else if (props.userType === LOCAL_CONSTANT.USER_TYPE.RR) {
                color = props.ReadOnlyObj[props.labelName] ? TextDisabledColor : TextDefaultColor;
            }
        }
        return color;
    };

    const changeBGColor = () => {
        let hasError = false;
        // 入力チェックエラーあり
        if (objRequestErrorList.length > 0) {
            hasError = objRequestErrorList.some(
                (error:any) => error.page === setTablePhysicsName(props.pageName) && error.column === props.labelName
            );
            if (hasError) return BGErrorColor;
        }
        // 入力チェックエラーなし
        if (!hasError) {
            // S店(セットバック、立地調査)の出店条件の一部入力欄グレーアウト化
            const targetLocationType = ["06", "07", "08", "09"];
            if(
                (objLocationBase.plan_area_id.startsWith("X") || objLocationBase.plan_area_id.startsWith("Y")) &&
                (targetLocationType.includes(objLocationBase.location_type))
            ) {
                if(props.pageName === "出店条件") {
                    if(
                        props.labelName === "nearest_already_store_distance" || //最寄り既設店までの距離
                        props.labelName === "distance_standard_alreadystore_count" || //距離基準内の既設店舗数
                        props.labelName === "conflict_cvs" || //元競合CVS
                        props.labelName === "cvs_last_nippan" //CVS 直近日販
                    ){
                        return BGDisabledColor;
                    }
                }
            }
            if (props.disabled) {
                return BGDisabledColor;
            } else if (props.InputValueObj[props.labelName].length > 0) {
                return BGChangeColor;
            }
            return BGDefaultColor;
        }
    };

    //ユーザー種別（'z002'、'z004'、'z005'）が選択されている場合のTextFieldのスタイル調整
    const TextFieldSx = () => {
        return {
            "& .MuiOutlinedInput-input": {
                color: changeTextColor(),
                textAlign: "center" //入力した値を中央寄せ
            },
            //disabledがtrueのとき背景がグレー
            "& .Mui-disabled.MuiInputBase-root": {
                backgroundColor: BGDisabledColor,
                "&: hover": {
                    backgroundColor: BGDisabledColor
                }
            }
        };
    };

    //ユーザー種別（'z003'）が選択されている場合のTextFieldのスタイル調整
    const SelectZ003TextFieldSx = () => {
        const hasBorderColor = changeBorderColor();
        return {
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderDefaultColor,
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&:hover fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderHoverColor,    //ホバー時
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&.Mui-focused fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderFocusedColor,  //フォーカス時
                    borderWidth: hasBorderColor ? "3pt" : "2pt"                           //線の幅調整
                }
            },
            "& .MuiOutlinedInput-input": {
                color: changeTextColor(),
                textAlign: "center" //入力した値を中央寄せ
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                backgroundColor: changeBGColor()
            },
            cursor: props.ReadOnlyObj[props.labelName] ? "not-allowed" : "text" //鉛筆アイコンをクリック時にTextFieldにカーソルを表示
        };
    }; 

    //ユーザー種別（'z001'）が選択されている場合のTextFieldのスタイル調整
    const SelectZ001TextFieldSx = () => {
        const hasBorderColor = changeBorderColor();
        return {
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderDefaultColor,
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&:hover fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderHoverColor,    //ホバー時
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&.Mui-focused fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderFocusedColor,  //フォーカス時
                    borderWidth: hasBorderColor ? "3pt" : "2pt"                           //線の幅調整
                }
            },
            "& .MuiOutlinedInput-input": {
                color: changeTextColor(),
                textAlign: "center" //入力した値を中央寄せ
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                backgroundColor: changeBGColor()
            },
            cursor: props.ReadOnlyObj[props.labelName] ? "not-allowed" : "text" //鉛筆アイコンをクリック時にTextFieldにカーソルを表示
        };
    };

    //不備コメントの項目が登録されている場合、trueを返す borderColorを赤に変更
    const changeBorderColor = () => {
        let hasError = false;

        if (listDeficiencyComment.length === 0) {
            return hasError;
        }

        hasError = listDeficiencyComment.some(
            (deficiencyComment: any) => {
                if (props.pageName !== LOCAL_CONSTANT.NAVI.TRADE_AREA_CUT) {
                    return (
                        deficiencyComment.table_name === setTablePhysicsName((props.pageName)) &&
                        deficiencyComment.column_name === props.labelName &&
                        LOCAL_CONSTANT.DEFICIENCY.STATUS.includes(deficiencyComment.deficiency_status)
                    )
                } else {
                    return (
                        deficiencyComment.table_name === setTablePhysicsName(pageToTableMapFunc(props.pageName)) &&
                        deficiencyComment.column_name === props.labelName &&
                        LOCAL_CONSTANT.DEFICIENCY.STATUS.includes(deficiencyComment.deficiency_status)
                    )
                }
            });

        return hasError;
    };

    // 吹き出しスタイル
    const chatBubbleErrorSx = () => {
        const hasBorderColor = changeBorderColor();
        let backgroundColor = "";
        if (props.disabled) {
            backgroundColor = hasBorderColor ? BorderErrorColor : "#e0e0e0"; //disabledFlag === true 非活性
        } else {
            backgroundColor = hasBorderColor ? BorderErrorColor : "#1565c0"; //disabledFlag === false 活性
        }
        return {
            backgroundColor: backgroundColor,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "35px",
            height: "35px",
        };
    };

    return (
        <Box>
            {/* ユーザー種別がRFCとリサーチ以外の場合*/}
            {!LOCAL_CONSTANT.USER_TYPE.RFC_RR.includes(props.userType) && (
                <TextField
                    fullWidth
                    disabled={true}
                    value={props.InputValueObj[props.labelName]}
                    sx={TextFieldSx()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {props.startAdornment}
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {props.endAdornment}
                            </InputAdornment>
                        ),
                    }}
                    variant="outlined"
                />
            )}
            {/* ユーザー種別がRFCの場合 userTypeがz003*/}
            {props.userType === LOCAL_CONSTANT.USER_TYPE.RFC && (
                <Box display="flex" alignItems="center">
                    <TextField
                        fullWidth
                        value={props.InputValueObj[props.labelName]}
                        onClick={() => props.handleInputClick(props.labelName)}
                        onChange={(e) => props.handleInputChange(e, props.labelName)}
                        onBlur={(e) => props.handleInputBlur(e, props.labelName)}
                        sx={SelectZ003TextFieldSx()}
                        InputProps={{
                            readOnly: props.disabled,
                            startAdornment: (
                                <InputAdornment position="start">
                                    {props.startAdornment}
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {props.endAdornment}
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                    />
                    {/* アイコン全体の表示非表示 */}
                    {props.ShowIconsObj[props.labelName] && (
                        <Box sx={{ minWidth: "90px" }}>
                            {/* 吹き出しアイコン */}
                            <IconButton
                                disabled={props.disabled}
                                onClick={() => props.handleChatBubbleClick(props.labelName)}
                                sx={iconHover}
                            >
                                <Box sx={chatBubbleErrorSx()}>
                                    <ChatBubbleIcon sx={chatBubbleIconSx} />
                                </Box>
                            </IconButton>
                        </Box>
                    )}
                </Box>
            )}
            {/* ユーザー種別がリサーチの場合 userTypeがz001 */}
            {props.userType === LOCAL_CONSTANT.USER_TYPE.RR && (
                <Box display="flex" alignItems="center">
                    <TextField
                        fullWidth
                        value={props.InputValueObj[props.labelName]}
                        onClick={() => props.handleInputClick(props.labelName)}
                        onChange={(e) => props.handleInputChange(e, props.labelName)}
                        onBlur={(e) => props.handleInputBlur(e, props.labelName)}
                        inputRef={inputRef}
                        sx={SelectZ001TextFieldSx()}
                        InputProps={{
                            readOnly: props.ReadOnlyObj[props.labelName],
                            startAdornment: (
                                <InputAdornment position="start">
                                    {props.startAdornment}
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {props.endAdornment}
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                    />
                    {/* アイコン全体の表示非表示 */}
                    {props.ShowIconsObj[props.labelName] && (
                        <Box sx={{ minWidth: "90px" }}>
                            {/* 吹き出しアイコン */}
                            <IconButton
                                disabled={props.disabled}
                                onClick={() => props.handleChatBubbleClick(props.labelName)}
                                sx={iconHover}
                            >
                                <Box sx={chatBubbleErrorSx()}>
                                    <ChatBubbleIcon sx={chatBubbleIconSx} />
                                </Box>
                            </IconButton>
                            {/* 鉛筆アイコンの表示非表示 */}
                            {props.ShowEditIconObj[props.labelName] && (
                                <IconButton onClick={() => handleEditIconClick(props.labelName)}
                                    sx={iconHover}
                                >
                                    <Box sx={editIconCircleSx}>
                                        <EditIcon />
                                    </Box>
                                </IconButton>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default InputWithIcons;
